import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { evaluationFrameworksApi } from '../services/api';
import { EvaluationFrameworkCreate, EvaluationFrameworkUpdate, EvaluationFramework } from '../types';

interface FrameworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingFramework?: EvaluationFramework | null;
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const FrameworkModal: React.FC<FrameworkModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingFramework,
  addNotification
}) => {
  const [formData, setFormData] = useState<EvaluationFrameworkCreate>({
    name: '',
    description: '',
    framework_type: 'custom',
    version: '',
    status: 'active',
    documentation_url: '',
    github_url: '',
    api_endpoint: '',
    requires_api_key: false,
    supports_batch_evaluation: true,
    max_concurrent_evaluations: 10,
    default_timeout_seconds: 30,
    score_range_min: 0.0,
    score_range_max: 1.0,
    score_description: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [frameworkTypes, setFrameworkTypes] = useState<string[]>([]);
  const [frameworkStatuses, setFrameworkStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadMetadata();
      if (editingFramework) {
        setFormData({
          name: editingFramework.name,
          description: editingFramework.description || '',
          framework_type: editingFramework.framework_type,
          version: editingFramework.version || '',
          status: editingFramework.status,
          documentation_url: editingFramework.documentation_url || '',
          github_url: editingFramework.github_url || '',
          api_endpoint: editingFramework.api_endpoint || '',
          requires_api_key: editingFramework.requires_api_key,
          supports_batch_evaluation: editingFramework.supports_batch_evaluation,
          max_concurrent_evaluations: editingFramework.max_concurrent_evaluations,
          default_timeout_seconds: editingFramework.default_timeout_seconds,
          score_range_min: editingFramework.score_range_min,
          score_range_max: editingFramework.score_range_max,
          score_description: editingFramework.score_description || ''
        });
      } else {
        // Reset form for new framework
        setFormData({
          name: '',
          description: '',
          framework_type: 'custom',
          version: '',
          status: 'active',
          documentation_url: '',
          github_url: '',
          api_endpoint: '',
          requires_api_key: false,
          supports_batch_evaluation: true,
          max_concurrent_evaluations: 10,
          default_timeout_seconds: 30,
          score_range_min: 0.0,
          score_range_max: 1.0,
          score_description: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, editingFramework]);

  const loadMetadata = async () => {
    try {
      const [types, statuses] = await Promise.all([
        evaluationFrameworksApi.getFrameworkTypes(),
        evaluationFrameworksApi.getFrameworkStatuses()
      ]);
      setFrameworkTypes(types);
      setFrameworkStatuses(statuses);
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Framework name is required';
    }

    if (!formData.framework_type) {
      newErrors.framework_type = 'Framework type is required';
    }

    if (formData.score_range_min !== undefined && formData.score_range_max !== undefined && formData.score_range_min >= formData.score_range_max) {
      newErrors.score_range_max = 'Max score must be greater than min score';
    }

    if (formData.max_concurrent_evaluations !== undefined && formData.max_concurrent_evaluations < 1) {
      newErrors.max_concurrent_evaluations = 'Max concurrent evaluations must be at least 1';
    }

    if (formData.default_timeout_seconds !== undefined && formData.default_timeout_seconds < 1) {
      newErrors.default_timeout_seconds = 'Timeout must be at least 1 second';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (editingFramework) {
        await evaluationFrameworksApi.updateFramework(editingFramework.id, formData);
        addNotification('Framework updated successfully', 'success');
      } else {
        await evaluationFrameworksApi.createFramework(formData);
        addNotification('Framework created successfully', 'success');
      }
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving framework:', error);
      addNotification(error.response?.data?.detail || 'Failed to save framework', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof EvaluationFrameworkCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-slate-800 border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-100">
            {editingFramework ? 'Edit Framework' : 'Create New Framework'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
            <h3 className="text-lg font-medium text-slate-100 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Framework Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.name ? 'border-red-500' : 'border-slate-600'
                  }`}
                  placeholder="e.g., RAGAS, OpenAI Judge"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Framework Type *
                </label>
                <select
                  value={formData.framework_type}
                  onChange={(e) => handleInputChange('framework_type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.framework_type ? 'border-red-500' : 'border-slate-600'
                  }`}
                >
                  {frameworkTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.framework_type && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.framework_type}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => handleInputChange('version', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., 1.0.0, v2.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {frameworkStatuses.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Describe what this framework does and how it evaluates responses..."
                />
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
            <h3 className="text-lg font-medium text-slate-100 mb-4">Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Max Concurrent Evaluations
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_concurrent_evaluations}
                  onChange={(e) => handleInputChange('max_concurrent_evaluations', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.max_concurrent_evaluations ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.max_concurrent_evaluations && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.max_concurrent_evaluations}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Default Timeout (seconds)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.default_timeout_seconds}
                  onChange={(e) => handleInputChange('default_timeout_seconds', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.default_timeout_seconds ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.default_timeout_seconds && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.default_timeout_seconds}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Range Min
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.score_range_min}
                  onChange={(e) => handleInputChange('score_range_min', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Range Max
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.score_range_max}
                  onChange={(e) => handleInputChange('score_range_max', parseFloat(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.score_range_max ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
                {errors.score_range_max && (
                  <p className="mt-1 text-sm text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.score_range_max}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Score Description
                </label>
                <input
                  type="text"
                  value={formData.score_description}
                  onChange={(e) => handleInputChange('score_description', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., 0-1 scale, where 1.0 represents perfect performance"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requires_api_key"
                  checked={formData.requires_api_key}
                  onChange={(e) => handleInputChange('requires_api_key', e.target.checked)}
                  className="h-4 w-4 text-indigo-500 focus:ring-indigo-500 border-slate-600 rounded bg-slate-800"
                />
                <label htmlFor="requires_api_key" className="ml-2 text-sm text-slate-200">
                  Requires API Key
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="supports_batch_evaluation"
                  checked={formData.supports_batch_evaluation}
                  onChange={(e) => handleInputChange('supports_batch_evaluation', e.target.checked)}
                  className="h-4 w-4 text-indigo-500 focus:ring-indigo-500 border-slate-600 rounded bg-slate-800"
                />
                <label htmlFor="supports_batch_evaluation" className="ml-2 text-sm text-slate-200">
                  Supports Batch Evaluation
                </label>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
            <h3 className="text-lg font-medium text-slate-100 mb-4">Links & Resources</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Documentation URL
                </label>
                <input
                  type="url"
                  value={formData.documentation_url}
                  onChange={(e) => handleInputChange('documentation_url', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://docs.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  GitHub URL
                </label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => handleInputChange('github_url', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://github.com/example/repo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  API Endpoint
                </label>
                <input
                  type="url"
                  value={formData.api_endpoint}
                  onChange={(e) => handleInputChange('api_endpoint', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://api.example.com/v1"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : editingFramework ? 'Update Framework' : 'Create Framework'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FrameworkModal;
